import { useEffect, useMemo, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Lock, KeyRound, ArrowRight, ArrowLeft, Cpu, HardDrive, Radio, Battery,
  ShieldCheck, Zap, DollarSign, AlertTriangle, Server, Home, Briefcase,
  Wrench, Rocket, CheckCircle2, ChevronDown, Download, Layers, QrCode,
  Wifi, Database, Sparkles, Package, TrendingUp,
} from "lucide-react";
import { exportPitchPptx, flattenSlideForPptx } from "@/lib/exportPitchPptx";

const PIN = "2353";
const STORAGE_KEY = "endexia_pitch_unlocked";

// ============ CONTENT MODEL — 10 SLIDES ============
const SLIDES = [
  {
    id: 1,
    kicker: "// 01 · TITLE & VISION",
    tag: "THE FUTURE OF SOVEREIGN INTELLIGENCE",
    title: "Endexia Flow — the <span class=\"text-cyan-400\">plug-and-play</span> edge AI appliance.",
    body:
      "A sleek, self-contained AI appliance for homes, offices, and workshops. Zero terminal commands. 100% private local intelligence with zero cloud data leaks. Unified Matter smart-building automation — all in a single box that ships ready to plug into the wall.",
    bullets: [
      "Zero terminal commands · zero cloud dependency · zero per-token fees",
      "Enterprise-grade AI compute in a chassis smaller than a router",
      "Matter-native building automation controls thousands of smart devices",
      "60-second QR-scan onboarding — as simple as pairing wireless earbuds",
    ],
  },
  {
    id: 2,
    kicker: "// 02 · THE PROBLEM",
    tag: "MARKET PAIN POINTS",
    title: "Why cloud AI <span class=\"text-cyan-400\">fails</span> modern local businesses.",
    body:
      "Small businesses and smart-space owners are trapped between two bad options: paying endless per-token cloud fees with zero privacy, or hiring an engineer just to set up a local model.",
    problems: [
      {
        tag: "DATA LEAKAGE",
        title: "Privacy Risk & Compliance Liability",
        desc: "Uploading confidential client records, legal files, and financial documents to public cloud AI creates massive HIPAA / SOC2 / attorney-client exposure that no professional firm should carry.",
        icon: AlertTriangle,
      },
      {
        tag: "COST DRIFT",
        title: "The Per-Token Subscription Trap",
        desc: "Recurring API charges and monthly SaaS bills scale exponentially with usage. A team of 10 heavy users can run $8k–$15k/year — and the meter never stops.",
        icon: DollarSign,
      },
      {
        tag: "TECHNICAL WALL",
        title: "Intimidating Setup Barrier",
        desc: "Existing open-source local AI requires terminal knowledge, Docker containers, driver compilations, and server hardware. Non-engineers simply cannot deploy it.",
        icon: Cpu,
      },
    ],
  },
  {
    id: 3,
    kicker: "// 03 · THE SOLUTION",
    tag: "THE PRODUCT BREAKTHROUGH",
    title: "Enterprise AI in a box. <span class=\"text-cyan-400\">Dummy simple.</span>",
    body:
      "Endexia Flow removes the friction entirely. Take it out of the box, plug it in, scan the QR code with your phone, and your entire office or house now has a dedicated private intelligence core.",
    solution: [
      {
        title: "True Plug-and-Play",
        desc: "Plug into power near your router or run untethered on internal LiFePO4 battery power. No config files. No SSH. No Wi-Fi setup screen.",
        icon: Package,
      },
      {
        title: "Phone-as-a-Bridge Setup",
        desc: "Scan the laser-etched QR code on the chassis with any smartphone camera to auto-configure the entire system in under 60 seconds via BLE.",
        icon: QrCode,
      },
      {
        title: "Complete Data Sovereignty",
        desc: "All computations, vector embeddings, and voice controls run 100% locally on dedicated NVIDIA Jetson Orin edge hardware. Nothing leaves the box.",
        icon: ShieldCheck,
      },
    ],
  },
  {
    id: 4,
    kicker: "// 04 · 60-SECOND ONBOARDING",
    tag: "USER EXPERIENCE",
    title: "From box to autonomous operations in <span class=\"text-cyan-400\">3 steps</span>.",
    body:
      "If you can connect a pair of wireless headphones, you can deploy an enterprise-grade AI server with Endexia Flow. The onboarding experience is intentionally boring.",
    steps: [
      {
        n: "01",
        label: "Power Up",
        desc: "Connect to wall power or toggle battery mode for field operations. The status ring glows steady amber while the Vault initializes.",
        icon: Battery,
      },
      {
        n: "02",
        label: "Scan & Bridge",
        desc: "Scan the laser-etched QR code on the chassis. Your phone securely bridges Wi-Fi credentials and admin keys via Bluetooth Low Energy 5.3.",
        icon: QrCode,
      },
      {
        n: "03",
        label: "Instant Dashboard",
        desc: "Launch the local web dashboard from any phone, tablet, or laptop on the network at flow.local — everyone on the LAN now has access.",
        icon: Sparkles,
      },
    ],
  },
  {
    id: 5,
    kicker: "// 05 · CORE PILLARS",
    tag: "PLATFORM CAPABILITIES",
    title: "Dual engine: <span class=\"text-cyan-400\">cognitive intelligence</span> meets <span class=\"text-violet-400\">physical automation</span>.",
    body:
      "Flow is not just a chatbot. It bridges language models directly to the physical world using the universal Matter standard, allowing it to control your physical space intelligently.",
    pillars: [
      {
        side: "cyan",
        tag: "PRIVATE BRAIN",
        title: "Local Vector RAG · Document Search",
        icon: Database,
        capabilities: [
          "Drag-and-drop PDFs, manuals, schematics, invoices, contracts",
          "Instant cited answers in plain English — no manual searching",
          "Runs Llama 3.1 8B / Mistral 7B / Phi-3.5 locally on Tensor Cores",
          "Voice interface via local Whisper — zero cloud roundtrip",
        ],
      },
      {
        side: "violet",
        tag: "MATTER HUB",
        title: "Smart Home & Building Automation",
        icon: Radio,
        capabilities: [
          "Native Matter + Thread controller — thousands of devices supported",
          "Universal control across smart plugs, thermostats, locks, sensors",
          "Voice-driven context automations ('lock the shop, kill tool power')",
          "Building automation without a separate hub or subscription",
        ],
      },
    ],
  },
  {
    id: 6,
    kicker: "// 06 · HARDWARE & EDGE ARCHITECTURE",
    tag: "ENGINEERING EXCELLENCE",
    title: "Powered by <span class=\"text-cyan-400\">NVIDIA Jetson Orin</span> edge compute.",
    body:
      "We engineered Flow on proven industrial compute. The NVIDIA Jetson platform provides massive neural processing while consuming less power than a standard light bulb.",
    hardware: [
      { icon: Cpu,       label: "EDGE BRAIN",     tech: "NVIDIA Jetson Orin (Nano 8GB / NX 16GB)", spec: "40–100 TOPS · 20–40W TDP" },
      { icon: HardDrive, label: "STORAGE & RAG",  tech: "2TB NVMe PCIe 4.0 SSD",                    spec: "Encrypted local knowledge base" },
      { icon: Wifi,      label: "CONNECTIVITY",   tech: "Gigabit Ethernet · Wi-Fi 6E · BLE 5.3",    spec: "Unified radio array" },
      { icon: Radio,     label: "IOT BRIDGE",     tech: "Matter-over-Thread / Zigbee 3.0",           spec: "Native building automation" },
      { icon: Battery,   label: "POWER SYSTEM",   tech: "USB-C PD 65W + LiFePO4 pack",              spec: "Dual mode · hot-swappable" },
      { icon: Server,    label: "OS RUNTIME",     tech: "Ubuntu 22.04 LTS · JetPack 6.x",           spec: "Hardened · auto-recovery watchdog" },
    ],
  },
  {
    id: 7,
    kicker: "// 07 · TARGET MARKET",
    tag: "MARKET OPPORTUNITY",
    title: "A massive unaddressed market for <span class=\"text-cyan-400\">sovereign edge computing</span>.",
    body:
      "Every professional firm handling sensitive data and every smart space seeking privacy is our customer. Multi-billion dollar shift from centralized cloud to sovereign edge is already in motion.",
    personas: [
      {
        icon: Briefcase,
        tag: "PROFESSIONAL SERVICES",
        title: "Clinics · Law Firms · Accountants",
        desc: "Medical practices, law firms, and accountants who must protect client records from cloud leaks. HIPAA-safe local AI removes their biggest compliance concern.",
        size: "~35M SMB professional firms in North America",
      },
      {
        icon: Wrench,
        tag: "WORKSHOPS & LOCAL RETAIL",
        title: "Mechanics · Contractors · Auto Shops",
        desc: "Mobile mechanics, auto shops, and industrial contractors requiring local diagnostics and automated facility management — including offline field operations.",
        size: "~5M skilled-trade & workshop operators",
      },
      {
        icon: Home,
        tag: "HIGH-PRIVACY SMART HOMES",
        title: "Tech Pros · Privacy-First Homeowners",
        desc: "Tech professionals and homeowners seeking intelligent home automation without Big Tech listening devices. Matter-native + local = zero third-party access.",
        size: "~15M privacy-conscious households (growing 22% YoY)",
      },
    ],
  },
  {
    id: 8,
    kicker: "// 08 · BUSINESS MODEL",
    tag: "MONETIZATION",
    title: "Hardware sales paired with <span class=\"text-cyan-400\">high-margin ecosystem</span>.",
    body:
      "Our core business generates healthy upfront hardware margins on every unit sold, with secondary revenue streams from specialized industry add-ons and multi-unit fleet software.",
    tiers: [
      { name: "Flow Base Unit",         price: "$1,499", tag: "APPLIANCE", note: "Jetson Orin Nano 8GB · 2TB NVMe · Wi-Fi 6E · Matter bridge",       popular: false },
      { name: "Flow Pro Enterprise",    price: "$2,999", tag: "APPLIANCE", note: "Jetson Orin NX 16GB · 4TB NVMe · Dual Ethernet · Extended battery", popular: true  },
      { name: "Modular Add-Ons",        price: "$99–$499", tag: "ACCESSORY", note: "Sensor packs · battery extensions · industrial model cartridges",   popular: false },
      { name: "Endexia OS Enterprise",  price: "$49 / mo / unit", tag: "SOFTWARE", note: "Multi-location fleet management · priority firmware updates", popular: false },
    ],
  },
  {
    id: 9,
    kicker: "// 09 · PRODUCTION ROADMAP",
    tag: "EXECUTION TIMELINE",
    title: "From working prototype to <span class=\"text-cyan-400\">full-scale production</span>.",
    body:
      "The prototype is working. We have proven the core OS and Matter integration. Our next phase is rolling out field pilot units and preparing for volume production.",
    roadmap: [
      { quarter: "Q1", title: "Functional Prototype Validated",  items: ["Jetson Orin JetPack image w/ TensorRT acceleration", "Headless BLE pairing daemon & QR provisioning", "Local model quantization (Llama 3.1 / Mistral / Phi-3.5)", "Matter-over-Thread bridge integration"], status: "done" },
      { quarter: "Q2", title: "Beta Pilot Program",              items: ["Deploy 25 test units: offices · auto shops · smart homes", "Voice interface + local Whisper STT hardening", "Vector RAG stress testing across mixed document sets", "Field-mode battery life certification"], status: "active" },
      { quarter: "Q3", title: "Tooling & Enclosure Production",  items: ["Injection-molded enclosure design finalization", "Thermal ducting certification for continuous 40W load", "FCC / CE regulatory certification path", "Manufacturing partner selection & tooling deposit"], status: "upcoming" },
      { quarter: "Q4", title: "Commercial Rollout",              items: ["Direct-to-consumer pre-order launch", "B2B distributor onboarding (professional services)", "Endexia OS Enterprise fleet dashboard GA", "First 1,000-unit production run"], status: "upcoming" },
    ],
  },
  {
    id: 10,
    kicker: "// 10 · THE ASK",
    tag: "PARTNERSHIP & INVESTMENT",
    title: "Join us in building the <span class=\"text-cyan-400\">sovereign intelligence</span> standard.",
    body:
      "The transition to private, edge-native AI is inevitable. Endexia Flow is the appliance that makes it accessible to everyone. Let us show you a live demo today.",
    ask: {
      raise: "$1.5M",
      round: "Seed Round",
      allocation: [
        { pct: 50, label: "Hardware Tooling & Inventory",   color: "cyan"    },
        { pct: 30, label: "Software & OS Development",       color: "violet"  },
        { pct: 20, label: "Growth & B2B Distribution",       color: "emerald" },
      ],
      cta: [
        { label: "contact@endexia.tech",       kind: "email" },
        { label: "https://endexia.tech/",      kind: "url"   },
      ],
    },
  },
];

// ============ PIN LOCK ============
function PinLock({ onUnlock }) {
  const [entered, setEntered] = useState(["", "", "", ""]);
  const [error, setError] = useState(false);

  const handleChange = (i, val) => {
    if (val && !/^\d$/.test(val)) return;
    const next = [...entered];
    next[i] = val;
    setEntered(next);
    setError(false);
    if (val && i < 3) document.getElementById(`endx-pin-${i + 1}`)?.focus();
    if (next.join("").length === 4) {
      if (next.join("") === PIN) {
        sessionStorage.setItem(STORAGE_KEY, "1");
        setTimeout(onUnlock, 300);
      } else {
        setError(true);
        setTimeout(() => setEntered(["", "", "", ""]), 400);
      }
    }
  };

  const handleKey = (i, e) => {
    if (e.key === "Backspace" && !entered[i] && i > 0) document.getElementById(`endx-pin-${i - 1}`)?.focus();
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center relative overflow-hidden" data-testid="endexia-pin-lock">
      <div className="absolute inset-0 te-grid-bg opacity-25" aria-hidden />
      <motion.div initial={{opacity:0,scale:0.6}} animate={{opacity:1,scale:1}} transition={{duration:2.2}} className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-cyan-500/15 blur-[140px]" />
      <motion.div initial={{opacity:0,scale:0.5}} animate={{opacity:1,scale:1}} transition={{duration:2.6,delay:0.3}} className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-violet-500/15 blur-[140px]" />

      <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.8}} className="relative bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-3xl p-10 max-w-md w-full mx-6 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)]">
        <div className="flex items-center justify-center mb-6">
          <div className="h-16 w-16 rounded-2xl bg-cyan-500/10 border border-cyan-500/40 flex items-center justify-center">
            <Lock size={26} className="text-cyan-400" />
          </div>
        </div>
        <div className="text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-3">// CONFIDENTIAL · PIN REQUIRED</div>
          <h1 className="font-display text-3xl font-bold tracking-tighter">Endexia Flow · Pitch Vault</h1>
          <p className="mt-3 text-sm text-slate-400 leading-relaxed">
            This deck contains confidential engineering, market, and financial information. Enter your 4-digit access PIN to unlock.
          </p>
        </div>

        <div className="mt-8 flex items-center justify-center gap-3">
          {entered.map((v, i) => (
            <input
              key={i}
              id={`endx-pin-${i}`}
              data-testid={`endexia-pin-${i}`}
              value={v}
              onChange={(e) => handleChange(i, e.target.value.slice(-1))}
              onKeyDown={(e) => handleKey(i, e)}
              inputMode="numeric"
              maxLength={1}
              type="password"
              autoComplete="off"
              className={`h-16 w-14 text-center font-display text-3xl font-bold rounded-2xl bg-slate-950 border-2 outline-none transition-all ${error ? "border-red-500 animate-pulse" : v ? "border-cyan-400 shadow-[0_0_15px_-3px_rgba(34,211,238,0.5)]" : "border-slate-800 focus:border-cyan-500/60"}`}
            />
          ))}
        </div>

        {error && (
          <motion.div initial={{opacity:0}} animate={{opacity:1}} className="mt-4 text-center font-mono text-[11px] uppercase tracking-[0.22em] text-red-400">
            // ACCESS DENIED · INVALID PIN
          </motion.div>
        )}

        <div className="mt-8 pt-6 border-t border-slate-800/60 flex items-center justify-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
          <KeyRound size={11} /> Session-locked · closes with your browser tab
        </div>
      </motion.div>
    </div>
  );
}

// ============ EXECUTIVE SUMMARY ============
function ExecutiveSummary() {
  return (
    <section className="relative overflow-hidden border-b border-slate-800/80" data-testid="endexia-summary">
      <div className="absolute inset-0 te-grid-bg opacity-30" aria-hidden />
      <motion.div initial={{opacity:0,scale:0.6}} animate={{opacity:1,scale:1}} transition={{duration:2.2}} className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-cyan-500/15 blur-[140px]" />
      <motion.div initial={{opacity:0,scale:0.5}} animate={{opacity:1,scale:1}} transition={{duration:2.6,delay:0.3}} className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-violet-500/15 blur-[140px]" />

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-14 lg:pb-20">
        <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} transition={{duration:0.6}} className="inline-flex items-center gap-2 border border-cyan-400/40 bg-cyan-500/[0.06] text-cyan-400 px-3 py-1.5 rounded-full font-mono text-[10px] uppercase tracking-[0.22em]">
          <ShieldCheck size={12} /> UNLOCKED · Endexia · Confidential Investor Deck
        </motion.div>

        <motion.h1 initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.9,delay:0.1,ease:[0.22,1,0.36,1]}} className="mt-6 font-display text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.98]">
          Endexia Flow · <span className="text-cyan-400">Edge AI Appliance</span>.
        </motion.h1>

        <motion.p initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.8,delay:0.25}} className="mt-6 text-base sm:text-lg text-slate-300 max-w-4xl leading-relaxed">
          A turnkey edge AI appliance (&ldquo;AI in a Box&rdquo;) that reimagines local artificial intelligence for non-technical operators. NVIDIA Jetson Orin compute, 2TB encrypted local RAG, and native Matter smart-building control — packaged in a sleek chassis with a <span className="text-cyan-400 font-semibold">60-second QR onboarding flow</span>. <span className="text-cyan-400 font-semibold">100% offline</span>, <span className="text-cyan-400 font-semibold">zero token fees</span>, <span className="text-violet-400 font-semibold">complete data sovereignty</span>. Raising <span className="text-cyan-400 font-semibold">$1.5M seed</span> to scale tooling and fulfill commercial pre-orders.
        </motion.p>

        {/* TL;DR grid */}
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { k: "// THE PRODUCT",  t: "AI-in-a-Box · Plug-and-Play", d: "Jetson Orin edge appliance with dual-mode power (wall + LiFePO4) and QR-scan onboarding. Zero terminal commands.", accent: "cyan" },
            { k: "// THE MOAT",     t: "Sovereignty + Matter Native", d: "100% offline private LLMs paired with a Matter-over-Thread building automation hub. Nobody else ships both in one box.",     accent: "violet" },
            { k: "// THE MODEL",    t: "$1,499–$2,999 hardware", d: "Base + Pro appliance SKUs · modular hardware add-ons · optional Endexia OS Enterprise for multi-location fleet management.", accent: "cyan" },
            { k: "// THE ASK",      t: "$1.5M Seed Round", d: "50% tooling & inventory · 30% software/OS · 20% growth & B2B distribution. Prototype validated. Beta pilot underway.",              accent: "violet" },
          ].map((c, i) => (
            <motion.div key={i} initial={{opacity:0,y:20}} animate={{opacity:1,y:0}} transition={{duration:0.6,delay:0.3 + i*0.05}} className="bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-2xl p-5">
              <div className={`font-mono text-[10px] uppercase tracking-[0.22em] ${c.accent === "violet" ? "text-violet-400" : "text-cyan-400"}`}>{c.k}</div>
              <h3 className="mt-2 font-display text-lg font-bold">{c.t}</h3>
              <p className="mt-2 text-[13px] text-slate-400 leading-relaxed">{c.d}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <a href="#deck" data-testid="endexia-cta-view-deck" className="inline-flex items-center gap-2 bg-cyan-500 text-slate-950 px-6 py-3.5 rounded-2xl font-mono text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-cyan-400 hover:scale-[1.02] transition-all shadow-[0_0_30px_-8px_rgba(34,211,238,0.6)]">
            <ArrowRight size={13} /> View Full Deck
          </a>
          <a href="#deck" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" }); }} className="inline-flex items-center gap-2 border border-slate-700 bg-slate-900/60 backdrop-blur-xl text-slate-200 px-6 py-3.5 rounded-2xl font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-slate-800 transition-all">
            <ChevronDown size={13} /> Scroll Through
          </a>
        </div>
      </div>
    </section>
  );
}

// ============ INFOGRAPHIC ============
function Infographic() {
  return (
    <section className="relative py-16 lg:py-24 border-b border-slate-800/80" data-testid="endexia-infographic">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.7}}>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-400 mb-4">// AT-A-GLANCE INFOGRAPHIC</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter max-w-4xl">The Vault, <span className="text-cyan-400">at a glance</span>.</h2>
        </motion.div>

        {/* KPI row */}
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { k: "40–100", v: "TOPS AI compute", Icon: Cpu, color: "text-cyan-400" },
            { k: "60 sec",  v: "onboarding time", Icon: QrCode, color: "text-violet-400" },
            { k: "$0",      v: "monthly token fees", Icon: DollarSign, color: "text-cyan-400" },
            { k: "100%",    v: "offline data sovereignty", Icon: ShieldCheck, color: "text-violet-400" },
          ].map((s, i) => (
            <motion.div key={i} initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.5,delay:i*0.06}} className="bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-2xl p-5">
              <s.Icon size={16} className={s.color} />
              <div className={`mt-3 font-display text-4xl lg:text-5xl font-bold tracking-tighter ${s.color}`}>{s.k}</div>
              <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">{s.v}</div>
            </motion.div>
          ))}
        </div>

        {/* Cost comparison + Setup flow */}
        <div className="mt-4 grid lg:grid-cols-2 gap-4">
          <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6}} className="bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-2xl p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-400">// 5-YEAR COST DELTA</div>
            <h3 className="mt-2 font-display text-2xl font-bold tracking-tight">Break-even on <span className="text-cyan-400">month 12</span>. After that, $0/mo forever.</h3>

            <div className="mt-6 space-y-4">
              <div>
                <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] mb-1.5">
                  <span className="text-red-400 flex items-center gap-1.5"><TrendingUp size={11} /> Cloud AI · per-token</span>
                  <span className="text-slate-100 font-bold">~$15,000 / yr</span>
                </div>
                <div className="h-3 rounded-full bg-slate-950 border border-slate-800 overflow-hidden">
                  <motion.div initial={{width:0}} whileInView={{width:"100%"}} viewport={{once:true}} transition={{duration:1.2,ease:"easeOut"}} className="h-full bg-gradient-to-r from-red-600 to-red-400" />
                </div>
              </div>
              <div>
                <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] mb-1.5">
                  <span className="text-cyan-400 flex items-center gap-1.5"><ShieldCheck size={11} /> Endexia Flow · one-time</span>
                  <span className="text-slate-100 font-bold">$2,999 (Pro)</span>
                </div>
                <div className="h-3 rounded-full bg-slate-950 border border-slate-800 overflow-hidden">
                  <motion.div initial={{width:0}} whileInView={{width:"20%"}} viewport={{once:true}} transition={{duration:1.4,delay:0.3,ease:"easeOut"}} className="h-full bg-gradient-to-r from-cyan-600 to-cyan-400" />
                </div>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="border border-cyan-500/30 bg-cyan-500/[0.05] rounded-xl p-4">
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-cyan-400">Year-1 savings</div>
                <div className="mt-1 font-display text-2xl font-bold text-cyan-400">$12,000+</div>
              </div>
              <div className="border border-cyan-500/30 bg-cyan-500/[0.05] rounded-xl p-4">
                <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-cyan-400">5-year savings</div>
                <div className="mt-1 font-display text-2xl font-bold text-cyan-400">$72,000+</div>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.6,delay:0.1}} className="bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-2xl p-6">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-400">// 60-SEC ONBOARDING</div>
            <h3 className="mt-2 font-display text-2xl font-bold tracking-tight">Plug in. Scan QR. <span className="text-cyan-400">Done.</span></h3>

            <div className="mt-6 space-y-3">
              {[
                { n: "01", t: "PLUG IN",  d: "Wall power or internal LiFePO4 battery", Icon: Battery },
                { n: "02", t: "SCAN QR",  d: "Phone camera → BLE bridge → Wi-Fi handoff", Icon: QrCode },
                { n: "03", t: "READY",    d: "Any device on LAN hits flow.local", Icon: Sparkles },
              ].map((s) => (
                <div key={s.n} className="flex items-center gap-4 border border-cyan-500/40 bg-cyan-500/[0.04] rounded-xl p-3.5">
                  <div className="h-10 w-10 rounded-lg bg-cyan-500/10 border border-cyan-500/50 flex items-center justify-center font-mono text-[11px] font-bold text-cyan-400 shrink-0">{s.n}</div>
                  <div className="flex-1">
                    <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-400">{s.t}</div>
                    <div className="mt-0.5 text-[13px] text-slate-200">{s.d}</div>
                  </div>
                  <s.Icon size={16} className="text-cyan-400" />
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// ============ SLIDE RENDERERS ============
function SlideBody({ slide }) {
  const heading = (
    <>
      {slide.tag && <div className="inline-flex items-center gap-2 border border-cyan-400/40 bg-cyan-500/[0.06] text-cyan-400 px-2.5 py-1 rounded-full font-mono text-[9px] uppercase tracking-[0.22em] mb-4">{slide.tag}</div>}
      <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-cyan-400 mb-3">{slide.kicker}</div>
      <h2 className="font-display text-3xl sm:text-4xl lg:text-6xl font-bold tracking-tighter leading-[1.02]" dangerouslySetInnerHTML={{ __html: slide.title }} />
      <p className="mt-5 text-slate-400 text-base sm:text-lg leading-relaxed max-w-3xl">{slide.body}</p>
    </>
  );

  // 1: bullets
  if (slide.bullets) {
    return (
      <div>
        {heading}
        <div className="mt-8 grid sm:grid-cols-2 gap-3">
          {slide.bullets.map((b, i) => (
            <div key={i} className="flex items-start gap-3 bg-slate-900/40 border border-slate-800/60 rounded-2xl p-4">
              <CheckCircle2 size={16} className="text-cyan-400 mt-0.5 shrink-0" />
              <span className="text-sm text-slate-200 leading-relaxed">{b}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // 2: problems
  if (slide.problems) {
    return (
      <div>
        {heading}
        <div className="mt-8 grid md:grid-cols-3 gap-3">
          {slide.problems.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={i} className="rounded-2xl p-5 border-2 border-red-500/40 bg-red-500/[0.04]">
                <div className="h-9 w-9 rounded-xl border border-red-500/50 bg-red-500/10 flex items-center justify-center">
                  <Icon size={15} className="text-red-400" />
                </div>
                <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-red-400">{p.tag}</div>
                <h4 className="mt-1 font-display text-lg font-bold text-slate-100">{p.title}</h4>
                <p className="mt-2 text-[13px] text-slate-300 leading-relaxed">{p.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // 3: solution
  if (slide.solution) {
    return (
      <div>
        {heading}
        <div className="mt-8 grid md:grid-cols-3 gap-3">
          {slide.solution.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="rounded-2xl p-5 border border-cyan-500/40 bg-slate-900/50">
                <div className="h-10 w-10 rounded-xl bg-cyan-500/10 border border-cyan-500/50 flex items-center justify-center"><Icon size={16} className="text-cyan-400" /></div>
                <h4 className="mt-3 font-display text-lg font-bold text-slate-100">{s.title}</h4>
                <p className="mt-2 text-[13px] text-slate-300 leading-relaxed">{s.desc}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // 4: onboarding steps
  if (slide.steps) {
    return (
      <div>
        {heading}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3">
          {slide.steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="relative rounded-2xl p-5 border border-cyan-500/40 bg-slate-900/50">
                <div className="flex items-center justify-between">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-400">STEP {s.n}</div>
                  <Icon size={18} className="text-cyan-400" />
                </div>
                <div className="mt-3 font-display text-xl font-bold text-slate-100">{s.label}</div>
                <div className="mt-2 text-[13px] text-slate-300 leading-relaxed">{s.desc}</div>
                {i < slide.steps.length - 1 && <ArrowRight size={18} className="hidden md:block absolute -right-4 top-1/2 -translate-y-1/2 text-cyan-400" />}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // 5: pillars (cyan + violet)
  if (slide.pillars) {
    return (
      <div>
        {heading}
        <div className="mt-8 grid lg:grid-cols-2 gap-4">
          {slide.pillars.map((p, i) => {
            const Icon = p.icon;
            const isViolet = p.side === "violet";
            const box = isViolet
              ? "border-violet-500/50 shadow-[0_0_60px_-15px_rgba(167,139,250,0.35)]"
              : "border-cyan-500/50 shadow-[0_0_60px_-15px_rgba(34,211,238,0.35)]";
            const iconWrap = isViolet ? "bg-violet-500/10 border-violet-500/50" : "bg-cyan-500/10 border-cyan-500/50";
            const iconColor = isViolet ? "text-violet-400" : "text-cyan-400";
            return (
              <div key={i} className={`rounded-2xl p-6 border-2 bg-slate-900/50 ${box}`}>
                <div className={`h-10 w-10 rounded-xl border flex items-center justify-center ${iconWrap}`}><Icon size={16} className={iconColor} /></div>
                <div className={`mt-3 font-mono text-[10px] uppercase tracking-[0.22em] ${iconColor}`}>{p.tag}</div>
                <h3 className="mt-1 font-display text-2xl font-bold text-slate-100">{p.title}</h3>
                <ul className="mt-4 space-y-2">
                  {p.capabilities.map((c, j) => (
                    <li key={j} className="flex items-start gap-2 text-[13px] text-slate-300 leading-relaxed">
                      <CheckCircle2 size={12} className={`${iconColor} mt-1 shrink-0`} /><span>{c}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // 6: hardware
  if (slide.hardware) {
    return (
      <div>
        {heading}
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-3">
          {slide.hardware.map((h, i) => {
            const Icon = h.icon;
            return (
              <div key={i} className="bg-slate-900/50 border border-slate-800/60 rounded-2xl p-5">
                <div className="h-9 w-9 rounded-xl bg-cyan-500/10 border border-cyan-500/40 flex items-center justify-center"><Icon size={15} className="text-cyan-400" /></div>
                <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-400">{h.label}</div>
                <div className="mt-1 font-display text-base font-bold text-slate-100">{h.tech}</div>
                <div className="mt-1 text-[12px] text-slate-400">{h.spec}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // 7: personas
  if (slide.personas) {
    return (
      <div>
        {heading}
        <div className="mt-8 grid md:grid-cols-3 gap-3">
          {slide.personas.map((p, i) => {
            const Icon = p.icon;
            return (
              <div key={i} className="rounded-2xl p-5 border border-violet-500/40 bg-violet-500/[0.04]">
                <div className="h-10 w-10 rounded-xl bg-violet-500/10 border border-violet-500/50 flex items-center justify-center"><Icon size={16} className="text-violet-400" /></div>
                <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.22em] text-violet-400">{p.tag}</div>
                <h4 className="mt-1 font-display text-lg font-bold text-slate-100">{p.title}</h4>
                <p className="mt-2 text-[13px] text-slate-300 leading-relaxed">{p.desc}</p>
                <div className="mt-4 pt-3 border-t border-slate-800/60 font-mono text-[10px] uppercase tracking-[0.18em] text-cyan-400">{p.size}</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // 8: pricing tiers
  if (slide.tiers) {
    return (
      <div>
        {heading}
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {slide.tiers.map((t, i) => {
            const highlight = t.popular;
            return (
              <div key={i} className={`rounded-2xl p-5 border-2 relative ${highlight ? "border-cyan-500/60 bg-slate-900/60 shadow-[0_0_60px_-15px_rgba(34,211,238,0.4)]" : "border-slate-800/60 bg-slate-900/40"}`}>
                {highlight && <div className="absolute -top-3 left-4 bg-cyan-500 text-slate-950 font-mono text-[9px] uppercase tracking-[0.22em] font-bold px-2 py-0.5 rounded">MOST POPULAR</div>}
                <div className={`font-mono text-[9px] uppercase tracking-[0.22em] ${highlight ? "text-cyan-400" : "text-slate-500"}`}>{t.tag}</div>
                <h4 className="mt-2 font-display text-lg font-bold text-slate-100">{t.name}</h4>
                <div className={`mt-3 font-display text-3xl font-bold ${highlight ? "text-cyan-400" : "text-slate-100"}`}>{t.price}</div>
                <p className="mt-3 text-[12px] text-slate-400 leading-relaxed">{t.note}</p>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // 9: roadmap
  if (slide.roadmap) {
    return (
      <div>
        {heading}
        <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-3">
          {slide.roadmap.map((q, i) => {
            const isDone = q.status === "done";
            const isActive = q.status === "active";
            const box = isDone
              ? "border-emerald-500/50 bg-emerald-500/[0.04]"
              : isActive
                ? "border-cyan-500/60 bg-cyan-500/[0.05] shadow-[0_0_40px_-15px_rgba(34,211,238,0.4)]"
                : "border-slate-800/60 bg-slate-900/50";
            const chipColor = isDone ? "text-emerald-400" : isActive ? "text-cyan-400" : "text-slate-500";
            const statusLabel = isDone ? "COMPLETE" : isActive ? "IN PROGRESS" : "UPCOMING";
            return (
              <div key={i} className={`rounded-2xl p-5 border-2 ${box}`}>
                <div className="flex items-center justify-between">
                  <div className={`font-mono text-[10px] uppercase tracking-[0.22em] font-bold ${chipColor}`}>{q.quarter}</div>
                  <div className={`font-mono text-[9px] uppercase tracking-[0.18em] ${chipColor}`}>{statusLabel}</div>
                </div>
                <h4 className="mt-3 font-display text-base font-bold text-slate-100">{q.title}</h4>
                <ul className="mt-3 space-y-1.5">
                  {q.items.map((it, j) => (
                    <li key={j} className="flex items-start gap-1.5 text-[11.5px] text-slate-300 leading-relaxed">
                      {isDone
                        ? <CheckCircle2 size={11} className="text-emerald-400 mt-0.5 shrink-0" />
                        : <ArrowRight   size={11} className={`${chipColor} mt-0.5 shrink-0`} />}
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // 10: the ask
  if (slide.ask) {
    const a = slide.ask;
    return (
      <div>
        {heading}
        <div className="mt-8 grid lg:grid-cols-2 gap-4">
          <div className="rounded-2xl p-6 border-2 border-cyan-500/50 bg-slate-900/50 shadow-[0_0_60px_-15px_rgba(34,211,238,0.35)]">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-cyan-400">// ROUND</div>
            <div className="mt-2 font-display text-lg font-bold text-slate-300">{a.round}</div>
            <div className="mt-3 font-display text-6xl font-bold text-cyan-400 tracking-tighter">{a.raise}</div>
            <div className="mt-6 space-y-3">
              {a.allocation.map((al, i) => {
                const colorMap = {
                  cyan:    { bar: "from-cyan-600 to-cyan-400",       text: "text-cyan-400" },
                  violet:  { bar: "from-violet-600 to-violet-400",   text: "text-violet-400" },
                  emerald: { bar: "from-emerald-600 to-emerald-400", text: "text-emerald-400" },
                }[al.color];
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em] mb-1.5">
                      <span className={colorMap.text}>{al.label}</span>
                      <span className="text-slate-100 font-bold">{al.pct}%</span>
                    </div>
                    <div className="h-2.5 rounded-full bg-slate-950 border border-slate-800 overflow-hidden">
                      <motion.div initial={{width:0}} whileInView={{width:`${al.pct}%`}} viewport={{once:true}} transition={{duration:1.2,ease:"easeOut"}} className={`h-full bg-gradient-to-r ${colorMap.bar}`} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="rounded-2xl p-6 border border-slate-800/80 bg-slate-900/50 flex flex-col">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-violet-400">// CONNECT WITH ENDEXIA</div>
            <h3 className="mt-2 font-display text-2xl font-bold text-slate-100">Ready for a live demo?</h3>
            <p className="mt-3 text-[13px] text-slate-400 leading-relaxed">
              Working prototype available. Beta pilot with 25 test units underway. We can walk you through the appliance in a 30-minute live session.
            </p>
            <div className="mt-5 space-y-2">
              {a.cta.map((c, i) => (
                <a
                  key={i}
                  href={c.kind === "email" ? `mailto:${c.label}` : c.label}
                  target={c.kind === "url" ? "_blank" : undefined}
                  rel={c.kind === "url" ? "noreferrer" : undefined}
                  className="flex items-center justify-between border border-cyan-500/30 hover:border-cyan-500/60 hover:bg-cyan-500/[0.05] transition-colors rounded-xl p-3.5"
                >
                  <span className="font-mono text-[13px] text-slate-200">{c.label}</span>
                  <ArrowRight size={14} className="text-cyan-400" />
                </a>
              ))}
            </div>
            <div className="mt-auto pt-6 flex flex-wrap gap-2">
              {["Prototype validated", "25-unit beta", "Matter certified path", "$1.5M seed target"].map((chip) => (
                <span key={chip} className="inline-flex items-center gap-1.5 border border-emerald-500/40 bg-emerald-500/[0.05] text-emerald-400 px-2.5 py-1 rounded-full font-mono text-[10px] uppercase tracking-[0.18em]">
                  <CheckCircle2 size={10} /> {chip}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

// ============ DECK ============
function Deck() {
  const [i, setI] = useState(0);
  const [printing, setPrinting] = useState(false);
  const next = useCallback(() => setI((v) => Math.min(SLIDES.length - 1, v + 1)), []);
  const prev = useCallback(() => setI((v) => Math.max(0, v - 1)), []);
  const slide = SLIDES[i];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === " ") { e.preventDefault(); next(); }
      if (e.key === "ArrowLeft") { e.preventDefault(); prev(); }
    };
    window.addEventListener("keydown", onKey);
    const beforePrint = () => setPrinting(true);
    const afterPrint = () => setPrinting(false);
    window.addEventListener("beforeprint", beforePrint);
    window.addEventListener("afterprint", afterPrint);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("beforeprint", beforePrint);
      window.removeEventListener("afterprint", afterPrint);
    };
  }, [next, prev]);

  return (
    <section id="deck" className="relative py-20 lg:py-28 scroll-mt-24" data-testid="endexia-deck">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <motion.div initial={{opacity:0,y:20}} whileInView={{opacity:1,y:0}} viewport={{once:true}} transition={{duration:0.7}}>
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-cyan-400 mb-4">// FULL PITCH DECK · {SLIDES.length} SLIDES</div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter max-w-4xl">The deck.</h2>
          <p className="mt-3 text-slate-400 text-sm">Use ← → arrow keys or click the pager below to navigate. Slide {i + 1} of {SLIDES.length}.</p>
        </motion.div>

        {printing ? (
          <div className="mt-8 space-y-6">
            {SLIDES.map((s) => (
              <div key={s.id} className="bg-slate-900/60 border border-slate-800/80 rounded-3xl p-6 lg:p-12" data-testid={`endexia-slide-${s.id}`}>
                <SlideBody slide={s} />
              </div>
            ))}
          </div>
        ) : (
          <>
            <div className="mt-8 relative bg-slate-900/60 border border-slate-800/80 backdrop-blur-xl rounded-3xl p-6 lg:p-12 min-h-[560px] overflow-hidden">
              <div className="absolute -top-24 -right-24 w-[400px] h-[400px] rounded-full bg-cyan-500/10 blur-[120px]" aria-hidden />
              <div className="absolute -bottom-24 -left-24 w-[400px] h-[400px] rounded-full bg-violet-500/10 blur-[120px]" aria-hidden />
              <AnimatePresence mode="wait">
                <motion.div key={slide.id} initial={{opacity:0, x:30}} animate={{opacity:1, x:0}} exit={{opacity:0, x:-30}} transition={{duration:0.35, ease:[0.22,1,0.36,1]}} className="relative" data-testid={`endexia-slide-${slide.id}`}>
                  <SlideBody slide={slide} />
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="mt-6 flex items-center justify-between pitch-no-print">
              <button onClick={prev} disabled={i === 0} data-testid="endexia-deck-prev" className="inline-flex items-center gap-2 border border-slate-700 bg-slate-900/60 backdrop-blur-xl text-slate-200 px-5 py-3 rounded-2xl font-mono text-[11px] uppercase tracking-[0.22em] hover:bg-slate-800 disabled:opacity-30 disabled:cursor-not-allowed transition-all">
                <ArrowLeft size={13} /> Prev
              </button>
              <div className="flex items-center gap-1.5">
                {SLIDES.map((_, idx) => (
                  <button key={idx} onClick={() => setI(idx)} data-testid={`endexia-deck-dot-${idx}`} aria-label={`Go to slide ${idx + 1}`} className={`h-2 rounded-full transition-all ${idx === i ? "w-8 bg-cyan-400" : "w-2 bg-slate-700 hover:bg-slate-500"}`} />
                ))}
              </div>
              <button onClick={next} disabled={i === SLIDES.length - 1} data-testid="endexia-deck-next" className="inline-flex items-center gap-2 bg-cyan-500 text-slate-950 px-5 py-3 rounded-2xl font-mono text-[11px] uppercase tracking-[0.22em] font-bold hover:bg-cyan-400 disabled:opacity-30 disabled:cursor-not-allowed transition-all shadow-[0_0_20px_-6px_rgba(34,211,238,0.6)]">
                Next <ArrowRight size={13} />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

// ============ MAIN PAGE ============
export default function EndexiaPitch() {
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(STORAGE_KEY) === "1") setUnlocked(true);
  }, []);

  const title = useMemo(() => "Endexia Flow · Pitch Vault", []);
  useEffect(() => {
    document.title = title;
    return () => { document.title = "TraceElements · We architect intelligent infrastructure."; };
  }, [title]);

  if (!unlocked) return <PinLock onUnlock={() => setUnlocked(true)} />;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="endexia-pitch-page">
      <ExecutiveSummary />
      <Infographic />
      <Deck />

      <section className="border-t border-slate-800/80 py-8">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-wrap items-center justify-between gap-4 pitch-no-print">
          <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
            // CONFIDENTIAL · Endexia · Prepared by TraceElements Technology Group
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => window.print()} data-testid="endexia-download-pdf" className="inline-flex items-center gap-2 bg-cyan-500 text-slate-950 hover:bg-cyan-400 px-4 py-2 rounded-xl font-mono text-[10px] uppercase tracking-[0.22em] font-bold transition-all shadow-[0_0_20px_-6px_rgba(34,211,238,0.6)]">
              <Download size={12} /> Download PDF
            </button>
            <button
              onClick={() => exportPitchPptx({
                vaultName: "Endexia Flow",
                title: "Endexia Flow · Edge AI Appliance",
                subtitle: "Plug-and-play edge AI for homes, offices, and workshops.",
                brand: "22D3EE",
                accent: "A78BFA",
                slides: SLIDES.map((s) => ({ kicker: s.kicker, title: s.title, body: s.body, bullets: flattenSlideForPptx(s) })),
              })}
              data-testid="endexia-download-pptx"
              className="inline-flex items-center gap-2 border border-cyan-500/60 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 px-4 py-2 rounded-xl font-mono text-[10px] uppercase tracking-[0.22em] font-bold transition-all"
            >
              <Download size={12} /> Download PPT
            </button>
            <button onClick={() => { sessionStorage.removeItem(STORAGE_KEY); window.location.reload(); }} data-testid="endexia-relock" className="inline-flex items-center gap-2 border border-slate-800 bg-slate-900/60 text-slate-400 hover:text-slate-200 hover:border-slate-700 px-4 py-2 rounded-xl font-mono text-[10px] uppercase tracking-[0.22em] transition-all">
              <Lock size={11} /> Relock Session
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
